import { useWorkspaceDataflowId } from "@/features/workspace/context/WorkspaceContext";
import { useMapStore } from "@/stores/map";
import type { UIStore, WorkspaceUIState } from "@/types/stores/ui";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

const initialWorkspaceUIState: WorkspaceUIState = {
  sidebar: {
    mode: "flow" as const,
    isOpen: true,
    onAction: undefined,
  },
  modal: {
    mode: null,
    isOpen: false,
    title: undefined,
    data: undefined,
  },
  view: "map" as const,
  mapModel: "stadia" as "stadia",
};

function getWorkspaceUIState(
  state: { workspaces: Record<string, WorkspaceUIState> },
  dataflowId: string | undefined
): WorkspaceUIState {
  if (!dataflowId || !state.workspaces[dataflowId]) {
    return initialWorkspaceUIState;
  }
  return state.workspaces[dataflowId];
}

function updateWorkspaceUI(
  state: { workspaces: Record<string, WorkspaceUIState> },
  dataflowId: string | undefined,
  updater: (workspaceUI: WorkspaceUIState) => WorkspaceUIState
): { workspaces: Record<string, WorkspaceUIState> } {
  if (!dataflowId) return state;
  const workspaceUI = getWorkspaceUIState(state, dataflowId);
  return {
    workspaces: {
      ...state.workspaces,
      [dataflowId]: updater(workspaceUI),
    },
  };
}

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      (set, get) => ({
        workspaces: {},
        globalModal: {
          mode: null,
          isOpen: false,
          title: undefined,
          data: undefined,
        },

        openSidebar: (dataflowId, mode, onAction) =>
          set(
            (state) =>
              updateWorkspaceUI(state, dataflowId, (workspaceUI) => ({
                ...workspaceUI,
                sidebar: { mode, isOpen: true, onAction },
              })),
            false,
            "ui/openSidebar"
          ),

        closeSidebar: (dataflowId) =>
          set(
            (state) =>
              updateWorkspaceUI(state, dataflowId, (workspaceUI) => ({
                ...workspaceUI,
                sidebar: { mode: null, isOpen: false, onAction: undefined },
              })),
            false,
            "ui/closeSidebar"
          ),

        toggleSidebar: (dataflowId, mode, onAction) =>
          set(
            (state) =>
              updateWorkspaceUI(state, dataflowId, (workspaceUI) => {
                const shouldClose =
                  workspaceUI.sidebar.isOpen &&
                  workspaceUI.sidebar.mode === mode;
                return {
                  ...workspaceUI,
                  sidebar: shouldClose
                    ? { mode: null, isOpen: false, onAction: undefined }
                    : { mode, isOpen: true, onAction },
                };
              }),
            false,
            "ui/toggleSidebar"
          ),

        openModal: (dataflowId, mode, title, data) => {
          if (!dataflowId) {
            set(
              (state) => ({
                ...state,
                globalModal: { mode, isOpen: true, title, data },
              }),
              false,
              "ui/openModal"
            );
          } else {
            set(
              (state) =>
                updateWorkspaceUI(state, dataflowId, (workspaceUI) => ({
                  ...workspaceUI,
                  modal: { mode, isOpen: true, title, data },
                })),
              false,
              "ui/openModal"
            );
          }
        },

        closeModal: (dataflowId) => {
          if (!dataflowId) {
            set(
              (state) => ({
                ...state,
                globalModal: {
                  mode: null,
                  isOpen: false,
                  title: undefined,
                  data: undefined,
                },
              }),
              false,
              "ui/closeModal"
            );
          } else {
            set(
              (state) =>
                updateWorkspaceUI(state, dataflowId, (workspaceUI) => ({
                  ...workspaceUI,
                  modal: {
                    mode: null,
                    isOpen: false,
                    title: undefined,
                    data: undefined,
                  },
                })),
              false,
              "ui/closeModal"
            );
          }
        },

        setView: (dataflowId, view) =>
          set(
            (state) =>
              updateWorkspaceUI(state, dataflowId, (workspaceUI) => ({
                ...workspaceUI,
                view,
                sidebar: { mode: null, isOpen: false },
              })),
            false,
            "ui/setView"
          ),

        setMapModel: (dataflowId, mapModel) =>
          set(
            (state) =>
              updateWorkspaceUI(state, dataflowId, (workspaceUI) => ({
                ...workspaceUI,
                mapModel,
              })),
            false,
            "ui/setMapModel"
          ),

        getWorkspaceUI: (dataflowId) => {
          const state = get();
          return getWorkspaceUIState(state, dataflowId);
        },

        getGlobalModal: () => {
          const state = get();
          return state.globalModal;
        },
      }),
      {
        name: "ui-store",
        partialize: (state) => {
          const persisted: Record<string, Partial<WorkspaceUIState>> = {};
          Object.entries(state.workspaces).forEach(([id, workspaceUI]) => {
            persisted[id] = {
              view: workspaceUI.view,
              mapModel: workspaceUI.mapModel,
            };
          });
          return { workspaces: persisted };
        },
      }
    ),
    {
      name: "ui-store",
    }
  )
);

import type {
  WorkspaceModalMode,
  WorkspaceSidebarMode,
} from "@/types/stores/ui";

const defaultSidebar: {
  mode: WorkspaceSidebarMode;
  isOpen: boolean;
  onAction?: () => void;
} = {
  mode: null,
  isOpen: false,
  onAction: undefined,
};
const defaultModal: {
  mode: WorkspaceModalMode;
  isOpen: boolean;
  title: undefined;
  data: undefined;
} = {
  mode: null,
  isOpen: false,
  title: undefined,
  data: undefined,
};

export const useSidebar = () => {
  const dataflowId = useWorkspaceDataflowId();
  return useUIStore((state) => {
    const workspaceUI = state.getWorkspaceUI(dataflowId);
    return workspaceUI?.sidebar ?? defaultSidebar;
  });
};

export const useModal = () => {
  const dataflowId = useWorkspaceDataflowId();
  return useUIStore((state) => {
    const workspaceUI = state.getWorkspaceUI(dataflowId);
    return workspaceUI?.modal ?? defaultModal;
  });
};

export const useView = () => {
  const dataflowId = useWorkspaceDataflowId();
  return useUIStore((state) => {
    const workspaceUI = state.getWorkspaceUI(dataflowId);
    return workspaceUI?.view ?? "map";
  });
};

export const useLayers = () => useMapStore((state) => state.staticLayers);
